(() => {
  const MAX_QUERY_LENGTH = 160;
  const DIAMETERS = new Set([
    '12', '14', '15', '16', '18', '20', '22', '25', '28', '30', '32', '35', '40', '45', '50', '54', '60',
    '63', '75', '80', '90', '100', '110', '125', '140', '160', '200', '250', '315'
  ]);
  const ANGLES = new Set(['15', '22', '22.5', '30', '45', '67', '67.5', '87', '87.5', '90']);
  const FITTING_CONTEXT = /\b(?:pvc|evacuation|evac|coude|raccord|manchon|reduction|te|tube|bouchon|selle|culotte|tampon|collier|gaine|polyethylene|pe)\b/;

  function compact(value) {
    return String(value || '')
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, MAX_QUERY_LENGTH);
  }

  function fold(value) {
    return compact(value)
      .toLocaleLowerCase('fr-FR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[’']/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeAliases(value) {
    let query = value;
    const replacements = [
      [/\b(?:bouton|plaque)\s+(?:de\s+)?(?:chasse(?:\s+d?\s*eau)?|wc)\b/g, 'plaque commande'],
      [/\b(?:ballon\s+(?:d\s+)?eau\s+chaude|cumulus)\b/g, 'chauffe eau'],
      [/\bchauffe[ -]?eau\b/g, 'chauffe eau'],
      [/\b(?:radiateur\s+(?:de\s+)?(?:sdb|salle\s+de\s+bains?)|seche[ -]?serviettes?)\b/g, 'seche serviettes'],
      [/\b(?:toilettes?|water[ -]?closet)\b/g, 'wc'],
      [/\b(?:tuyaux|tuyau)\b/g, 'tube'],
      [/\b(?:bouchon|bonde)\s+(?:de\s+)?lavabo\b/g, 'bonde lavabo'],
      [/\b(?:fixation|attache)\s+(?:pour\s+)?(?:tube|tuyau)\b/g, 'collier tube'],
      [/\brobinet\s+(?:de\s+)?lavabo\b/g, 'mitigeur lavabo'],
      [/\brobinet\s+lave[ -]?mains?\b/g, 'mitigeur lavabo'],
      [/\brobinet\s+(?:de\s+)?douche\b/g, 'mitigeur douche'],
      [/\brobinet\s+(?:de\s+)?baignoire\b/g, 'mitigeur baignoire'],
      [/\brobinet\s+bain[ -]?douche\b/g, 'mitigeur baignoire'],
      [/\brobinet\s+(?:de\s+)?cuisine\b/g, 'mitigeur cuisine'],
      [/\brobinet\s+(?:d[' ]?)?evier\b/g, 'mitigeur cuisine'],
      [/\bsalle\s+de\s+bains?\b/g, 'salle de bains'],
      [/\bsdb\b/g, 'salle de bains'],
      [/\bevac\b/g, 'evacuation'],
      [/\bfemelle\s*(?:[/.-]|\s)\s*femelle\b/g, 'ff'],
      [/\bmale\s*(?:[/.-]|\s)\s*male\b/g, 'mm'],
      [/\bmale\s*(?:[/.-]|\s)\s*femelle\b/g, 'mf'],
      [/\bfemelle\s*(?:[/.-]|\s)\s*male\b/g, 'fm'],
      [/\bf\s*(?:[/.-]|\s)\s*f\b/g, 'ff'],
      [/\bm\s*(?:[/.-]|\s)\s*m\b/g, 'mm'],
      [/\bm\s*(?:[/.-]|\s)\s*f\b/g, 'mf'],
      [/\bf\s*(?:[/.-]|\s)\s*m\b/g, 'fm']
    ];

    replacements.forEach(([pattern, replacement]) => {
      query = query.replace(pattern, replacement);
    });

    return query.replace(/\s+/g, ' ').trim();
  }

  function normalizeDimensions(value) {
    let query = value
      .replace(/\b(?:diametre|diam|dia|dn|d)\s*[:=-]?\s*(\d{2,3})(?:\s*mm)?\b/g, 'd$1')
      .replace(/(?:ø|⌀)\s*(\d{2,3})(?:\s*mm)?\b/g, 'd$1')
      .replace(/\b(\d{2,3})\s*mm\b/g, 'd$1');

    if (!FITTING_CONTEXT.test(query) || /\bd\d{2,3}\b/.test(query)) return query;

    const numericTokens = Array.from(query.matchAll(/\b(\d{2,3}(?:[.,]5)?)\b/g));
    if (!numericTokens.length) return query;

    const diameterCandidates = numericTokens.filter((match, index) => {
      const number = match[1].replace(',', '.');
      if (!DIAMETERS.has(number)) return false;
      if (numericTokens.length > 1 && index < numericTokens.length - 1 && ANGLES.has(number)) return false;
      return true;
    });

    const selected = diameterCandidates.at(-1);
    if (!selected) return query;

    const number = selected[1].replace(',', '.');
    if (numericTokens.length === 1 && ANGLES.has(number)) return query;

    const start = selected.index;
    return `${query.slice(0, start)}d${selected[1]}${query.slice(start + selected[1].length)}`;
  }

  function canonicalize(value) {
    return normalizeDimensions(normalizeAliases(fold(value))).replace(/\s+/g, ' ').trim();
  }

  function referenceToken(value) {
    const token = compact(value).replace(/\s*([._/-])\s*/g, '$1');
    if (!token || /\s/.test(token) || token.length > 100) return '';
    if (!/^[a-z0-9][a-z0-9._/-]*$/i.test(token)) return '';

    const hasLetters = /[a-z]/i.test(token);
    const hasDigits = /\d/.test(token);
    const hasReferencePunctuation = /[._/-]/.test(token);
    if ((hasLetters && hasDigits && token.length >= 5) || (hasReferencePunctuation && (token.match(/\d/g) || []).length >= 4) || /^\d{4,}$/.test(token)) {
      return token;
    }
    return '';
  }

  function naturalTerms(value) {
    return fold(value)
      .replace(/\b(?:bonjour|svp|merci|s il vous plait|s il te plait)\b/g, ' ')
      .replace(/\b(?:je|nous)\s+(?:cherche|cherchons|recherche|recherchons|veux|voulons|voudrais|voudrions|souhaite|souhaitons)\b/g, ' ')
      .replace(/\b(?:j ai|on a|nous avons)\s+besoin\b/g, ' ')
      .replace(/\b(?:est ce que|vous avez|avez vous|quelque chose|un truc)\b/g, ' ')
      .replace(/\b(?:un|une|des|du|de|d|le|la|les|l|pour|mon|ma|mes)\b/g, ' ')
      .replace(/[?!,;:()"<>]/g, ' ')
      .replace(/\s+/g, ' ').trim();
  }

  function referenceVariants(value) {
    const original = value.toUpperCase();
    const flat = original.replace(/[._/\s-]/g, '');
    const values = [original, flat, original.replace(/[._/\s-]+/g, '.')];
    const geberit = flat.match(/^(\d{3})(\d{3})([A-Z0-9]{2})(\d)$/);
    if (geberit) values.unshift(geberit.slice(1).join('.'));
    return [...new Set(values)].filter(Boolean);
  }

  function plan(value) {
    const original = compact(value);
    const token = referenceToken(original) || original.split(/\s+/).map(referenceToken).find(Boolean) || '';
    const strictReference = !!token && ((token.match(/\d/g) || []).length >= 4 || /[._/]/.test(token));
    const variants = token ? referenceVariants(token) : [];
    const exactQueries = variants.flatMap(ref => {
      const sku = `variants.sku:"${ref}"`;
      const manufacturer = `tag:"MPSB_REF_FABRICANT::${ref}"`;
      return /^\d+$/.test(ref) ? [sku, manufacturer, `variants.barcode:"${ref}"`] : [manufacturer, sku, `variants.barcode:"${ref}"`];
    });
    const exact = exactQueries[0] || '';
    const natural = naturalTerms(original);
    const canonical = canonicalize(natural);
    const alternatives = [...new Set([canonical, natural])].filter(Boolean);
    return { original, token, strictReference, exact, exactQueries, alternatives };
  }

  function build(value) {
    const queryPlan = plan(value);
    return queryPlan.exact || queryPlan.alternatives[0] || queryPlan.original;
  }

  const cache = new Map();

  async function request(query, exact, signal) {
    const key = `${exact ? 'exact' : 'natural'}:${query}`;
    const cached = cache.get(key);
    if (cached && Date.now() - cached.at < 120000) return cached.result;
    const searchUrl = routes.search_url || routes.predictive_search_url.replace(/\/suggest\/?$/, '');
    const url = new URL(exact ? searchUrl : routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    if (exact) {
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[fields]', 'title,product_type,variants.title,vendor,variants.sku,variants.barcode,body,tag');
      url.searchParams.set('options[unavailable_products]', 'show');
    } else {
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'show');
    }
    url.searchParams.set('section_id', exact ? 'mpsb-reference-search' : 'predictive-search');
    url.searchParams.set('mpsb_search_revision', '20261022-4');
    const timeout = AbortSignal.timeout(8000);
    const response = await fetch(url, { cache: 'no-store', signal: signal ? AbortSignal.any([signal, timeout]) : timeout });
    if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const section = doc.querySelector(exact ? '#shopify-section-mpsb-reference-search' : '#shopify-section-predictive-search');
    if (!section) throw new Error('Search section unavailable');
    const count = Number(section.querySelector('[data-mpsb-result-count]')?.dataset.mpsbResultCount || 0);
    const productCount = Number(section.querySelector('[data-mpsb-product-count]')?.dataset.mpsbProductCount || 0);
    const result = { query, markup: section.innerHTML, count, productCount, exact };
    if (cache.size >= 30) cache.delete(cache.keys().next().value);
    cache.set(key, { at: Date.now(), result });
    return result;
  }

  async function load(value, { signal } = {}) {
    const queryPlan = plan(value);
    if (queryPlan.exact) {
      let exactResult;
      for (const query of queryPlan.exactQueries) {
        exactResult = await request(query, true, signal);
        if (exactResult.count) return exactResult;
      }
      if (queryPlan.strictReference) return exactResult;
    }
    let result;
    for (const query of queryPlan.alternatives) {
      result = await request(query, false, signal);
      if (result.productCount) return result;
    }
    return result || request(queryPlan.original, false, signal);
  }

  window.MPSBSmartSearch = Object.freeze({ build, canonicalize, plan, load });
})();
